// ** Third Party Imports
import { type RouteObject, Navigate } from 'react-router-dom'

// ** Utility Imports
import { lazyImport } from '@/shared/lib'

// ** Layout Imports
import { BlankLayout } from '@/app/layouts/BlankLayout'
import { DashboardLayout } from '@/app/layouts/DashboardLayout'
import { LayoutWithSidebar } from '@/app/layouts/LayoutWithSidebar'
import { IsOwner } from '@/features/subscriptions/ui/IsOwner'
import { RequireSubscription } from '@/features/subscriptions/ui/RequireSubscription'
import { Redirect, RoleBasedDashboard } from '@/shared/ui'

// ** Route Imports
const { DashboardPage } = lazyImport(
	async () => await import('@/pages/dashboard'),
	'DashboardPage'
)
const { ClientManagementPage } = lazyImport(
	async () => await import('@/pages/client-management'),
	'ClientManagementPage'
)
const { CreateClientPage } = lazyImport(
	async () => await import('@/pages/client-management'),
	'CreateClientPage'
)
const { EditClientPage } = lazyImport(
	async () => await import('@/pages/client-management'),
	'EditClientPage'
)
const { AddTaxStrategyPage } = lazyImport(
	async () => await import('@/pages/add-tax-strategy'),
	'AddTaxStrategyPage'
)
const { CompanyCreatePage } = lazyImport(
	async () => await import('@/components/pages/company-form'),
	'CompanyCreatePage'
)
const { CompanyEditPage } = lazyImport(
	async () => await import('@/components/pages/company-form'),
	'CompanyEditPage'
)
const { BusinessSummaryPage } = lazyImport(
	async () => await import('@/pages/client-management'),
	'BusinessSummaryPage'
)
const { TaxStrategiesPage } = lazyImport(
	async () => await import('@/pages/tax-strategies'),
	'TaxStrategiesPage'
)
const { TaxStrategyPage } = lazyImport(
	async () => await import('@/pages/tax-strategies'),
	'TaxStrategyPage'
)
const { EditInfoPage } = lazyImport(
	async () => await import('@/pages/edit-tax-strategy'),
	'EditInfoPage'
)
const { CreatePlanPage } = lazyImport(
	async () => await import('@/pages/plan-management'),
	'CreatePlanPage'
)
const { EditPlanPage } = lazyImport(
	async () => await import('@/pages/plan-management'),
	'EditPlanPage'
)
const { PlanReportPage } = lazyImport(
	async () => await import('@/pages/plan-management'),
	'PlanReportPage'
)
const { ManagePlanDetailsPage } = lazyImport(
	async () => await import('@/pages/plan-management'),
	'ManagePlanDetailsPage'
)
const { AiReportPage } = lazyImport(
	async () => await import('@/pages/plan-management'),
	'AiReportPage'
)
const { ClientDashboardPage } = lazyImport(
	async () => await import('@/pages/client-dashboard'),
	'ClientDashboardPage'
)
const { UserProfilePage } = lazyImport(
	async () => await import('@/pages/user-profile'),
	'UserProfilePage'
)
const { Error404Page } = lazyImport(
	async () => await import('@/pages/error-404'),
	'Error404Page'
)
const { ChatHistoryPage } = lazyImport(
	async () => await import('@/pages/chat-history'),
	'ChatHistoryPage'
)
const { TaxStrategyTemplatePage } = lazyImport(
	async () => await import('@/pages/tax-strategies'),
	'TaxStrategyTemplatePage'
)
const { AnalyticsPage } = lazyImport(
	async () => await import('@/pages/analytics'),
	'AnalyticsPage'
)
const { SubscriptionPlanPage } = lazyImport(
	async () => await import('@/pages/subscription-plan'),
	'SubscriptionPlanPage'
)
const { ChoosePlanPage } = lazyImport(
	async () => await import('@/pages/subscription-plan/'),
	'ChoosePlanPage'
)
const { SubscriptionConfirmedSuccess } = lazyImport(
	async () => await import('@/pages/subscription-plan/'),
	'SubscriptionConfirmedSuccess'
)
const { SubscriptionCancelled } = lazyImport(
	async () => await import('@/pages/subscription-plan/'),
	'SubscriptionCancelled'
)
const { AdminUserStatisticsPage } = lazyImport(
	async () => await import('@/pages/admin-users-statistics'),
	'AdminUserStatisticsPage'
)
const { AdminDashboardPage } = lazyImport(
	async () => await import('@/pages/admin-dashboard'),
	'AdminDashboardPage'
)
const { UserDatabasePage } = lazyImport(
	async () => await import('@/pages/user-database'),
	'UserDatabasePage'
)
const { PriceManagementPage } = lazyImport(
	async () => await import('@/pages/price-management'),
	'PriceManagementPage'
)
const { AiChatbotPage } = lazyImport(
	async () => await import('@/pages/ai-chatbot'),
	'AiChatbotPage'
)
const { HelpPage } = lazyImport(
	async () => await import('@/pages/help'),
	'HelpPage'
)

export const protectedRoutes: RouteObject[] = [
	{
		element: <LayoutWithSidebar />,
		children: [
			{
				path: '/',
				element: (
					<RoleBasedDashboard>
						<DashboardPage />
					</RoleBasedDashboard>
				),
				children: [
					{
						// success modal after first time subscription/free trial
						path: '/subscription-success',
						element: <SubscriptionConfirmedSuccess />
					},
					{
						// success modal after free trial expiration
						path: '/subscription-confirmation',
						element: <SubscriptionConfirmedSuccess subscriptionStarted />
					}
				]
			},
			// ** /user-profile
			{
				path: '/user-profile',
				element: <DashboardLayout />,
				children: [
					{
						index: true,
						element: <UserProfilePage />
					},
					{
						path: 'chat-history/:clientId/:planId',
						element: <ChatHistoryPage />
					}
				]
			},
			// ** /client-management
			{
				path: '/client-management',
				element: <DashboardLayout />,
				children: [
					{
						index: true,
						element: <ClientManagementPage />
					},
					{
						path: 'create-client',
						element: (
							<RequireSubscription>
								<CreateClientPage />
							</RequireSubscription>
						)
					},
					{
						path: 'edit-client/:id',
						element: <EditClientPage />
					},
					{
						path: 'create-company/*',
						element: <CompanyCreatePage />
					},
					{
						path: 'edit-company/*',
						element: <CompanyEditPage />
					}
				]
			},
			// ** /client-dashboard
			{
				path: '/client-dashboard',
				element: <DashboardLayout />,
				children: [
					{
						index: true,
						element: <ClientDashboardPage />
					},
					{
						path: 'plan-details/:clientId/:planId',
						element: <ManagePlanDetailsPage />
					},
					{
						path: 'plan-details/:clientId/:planId/edit',
						element: <EditPlanPage />
					},
					{
						path: 'plan-details/:clientId/:planId/report',
						element: <PlanReportPage />
					},
					{
						path: 'plan-details/:clientId/:planId/ai-report',
						element: <AiReportPage />
					},
					{
						path: 'business-summary/:id',
						element: <BusinessSummaryPage />
					},
					{
						path: 'create-plan/*',
						element: <CreatePlanPage />
					}
				]
			},
			// ** /tax-strategies
			{
				path: '/tax-strategies',
				element: <DashboardLayout />,
				children: [
					{
						index: true,
						element: <TaxStrategiesPage />
					},
					{
						path: 'tax-strategy-details/:id',
						element: <TaxStrategyPage />
					},
					{
						path: '/tax-strategies/template/:templateId',
						element: <TaxStrategyTemplatePage />
					}
				]
			},
			{
				path: '/analytics',
				element: <DashboardLayout />,
				children: [
					{
						index: true,
						element: <AnalyticsPage />
					}
				]
			},
			{
				path: '/subscription-plan',
				element: <DashboardLayout />,
				children: [
					{
						index: true,
						element: (
							<IsOwner>
								<SubscriptionPlanPage />
							</IsOwner>
						)
					}
				]
			},
			{
				path: '/chat-bot',
				element: <Navigate to='/ai-chatbot' replace />
			},
			{
				path: '/ai-chatbot',
				element: <DashboardLayout />,
				children: [
					{
						index: true,
						element: <AiChatbotPage />
					}
				]
			},
			{
				path: '/help',
				element: <DashboardLayout />,
				children: [
					{
						index: true,
						element: <HelpPage />
					}
				]
			},
			// ** rest
			{
				path: '/login',
				element: <Redirect />
			},
			{
				path: '*',
				element: <Error404Page />
			}
		]
	},
	{
		path: '/admin',
		element: <LayoutWithSidebar />,
		children: [
			{
				element: <DashboardLayout />,
				children: [
					{
						path: 'dashboard',
						element: <AdminDashboardPage />
					},
					{
						path: 'user-database',
						element: <UserDatabasePage />
					},
					{
						path: 'users/statistics',
						element: <AdminUserStatisticsPage />
					},
					{
						path: 'price-management',
						element: <PriceManagementPage />
					},
					{
						path: 'tax-strategies',
						element: <DashboardLayout />,
						children: [
							{
								index: true,
								element: <TaxStrategiesPage />
							},
							{
								path: 'tax-strategy-details/:id',
								element: <TaxStrategyPage />
							},
							{
								path: 'tax-strategy-details/:id/info',
								element: <EditInfoPage />
							}
						]
					},
					{
						path: 'add-tax-strategy',
						element: <AddTaxStrategyPage />
					}
				]
			}
		]
	},
	{
		element: <BlankLayout />,
		children: [
			{
				path: '/choose-plan',
				element: <ChoosePlanPage />
			},
			// {
			// 	path: '/schedule',
			// 	element: <BookACallPage />
			// },
			{
				path: '/subscription-success',
				element: <SubscriptionConfirmedSuccess />
			},
			{
				path: '/subscription-canceled',
				element: <SubscriptionCancelled />
			}
		]
	}
]
